import { Button, ClassName, Label, Row, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import useSlackCertificationAccess from 'containers/settings/integrations/vendors/slack/hooks/useSlackCertificationAccess';
import { useToastDispatch } from 'contexts/ToastContext';
import useOrganizationUiSettingsUpdate from 'hooks/models/organization/settings/hooks/useOrganizationUiSettingsUpdate';
import { t } from 'i18next';
import { FunctionComponent, useState } from 'react';

export interface ICertificationsEnableFormProps {
  onRequestSubmitted: () => void;
  className?: ClassName;
}

const CertificationsEnableForm: FunctionComponent<ICertificationsEnableFormProps> = (props) => {
  const { className, onRequestSubmitted } = props;
  const toastDispatch = useToastDispatch();
  const [showErrorTip, setShowErrorTip] = useState(false);
  const certificationsAccess = useSlackCertificationAccess();
  const updateUiSettings = useOrganizationUiSettingsUpdate();

  const classes = classNames('certifications-enable-form', className);

  const onLinkClicked = () => {
    window.open('https://fast.wistia.com/embed/channel/9ytila0bxh');
  };

  const requestAccess = async () => {
    let requestSuccessful = false;
    try {
      const response = await certificationsAccess();
      requestSuccessful = response.status === 200;
    } catch (e) {
      requestSuccessful = false;
    }
    if (requestSuccessful) {
      updateUiSettings('requestedCertificationsAccess', true);
      onRequestSubmitted();
    } else {
      toastDispatch({
        type: 'OPEN',
        payload: { label: t('Failed to send request. Try again later.'), theme: Theme.DANGER },
      });
      setShowErrorTip(true);
    }
  };

  return (
    <div className={classes}>
      <Label className="mt-3 mb-2">{t('-Now Includes-')}</Label>
      {t('Certifications & Licenses')}
      <ul className="pl-4">
        <li>{t('Automated reminders before certifications expire')}</li>
        <li>{t('Employees upload credentials directly in the app')}</li>
        <li>
          {t('Supervisors see certification status during clock-in and assignment')}
        </li>
      </ul>
      <Label className="mt-3 mb-2">{t('Equipment Inspections')}</Label>
      <ul className="pl-4">
        <li>{t('Mobile pre-operation, post-operation, and routine inspections')}</li>
        <li>{t('Photo documentation and digital sign-off')}</li>
        <li>{t('Centralized inspection history for every piece of equipment')}</li>
      </ul>
      <Label className="mt-3 mb-2">{t('Resource Planning')}</Label>
      <ul className="pl-4">
        <li>{t('Visual planning for jobs, crews, and equipment')}</li>
        <li>{t('Identify conflicts before they impact work')}</li>
        <li>{t('One clear source of truth for field and office teams')}</li>
      </ul>
      <Label className="mt-7 mb-2">{t('Learn More')}</Label>
      <div>{t('Want a deeper look at each feature?')}</div>
      <div>
        {t('→ ')}
        <a className="link ml-1" onClick={onLinkClicked}>
          {t('Explore the Operations Module video series')}
        </a>
      </div>
      <Label className="mt-7 mb-2">{t('How to Get Started')}</Label>
      <div className="content">
        {t(
          `Click the button below and a BusyBusy team member will connect with you to walk through Operations and answer your questions. If you decide to move forward, we'll activate the Operations Module in your account.`
        )}
      </div>
      <Label className="mt-7 mb-2">{t('Pricing')}</Label>
      <div className="content">{t('Speak with our sales team for full pricing details.')}</div>
      <Row className="mt-9">
        <Button type="primary" onClick={requestAccess}>
          {t(`Yes - I'm Interested`)}
        </Button>
      </Row>

      <ConditionalContainer condition={showErrorTip}>
        <br />
        {t('* If you have an ad blocker, disable it and try again.')}
      </ConditionalContainer>
    </div>
  );
};

export default CertificationsEnableForm;
